import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import { dmy, msg } from './gstUi';

const prevMonth = () => {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
  return `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}`;
};
const STATUS = { sent: 'Sent', pending: 'Trying…', failed: 'Failed', reopened: 'Open for changes' };

export default function GstDeliveryBox() {
  const { call } = useGst();
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ caName: '', caWhatsapp: '', ownerWhatsapp: '', enabled: false, sendDay: 1 });
  const [month, setMonth] = useState(prevMonth());
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    call(() => gstAPI.delivery()).then((b) => {
      setSt(b.data);
      const s = b.data.settings;
      setF({ caName: (s.ca && s.ca.name) || '', caWhatsapp: (s.ca && s.ca.whatsapp) || '', ownerWhatsapp: s.ownerWhatsapp || '', enabled: !!s.enabled, sendDay: s.sendDay || 1 });
    }).catch((e) => toast.error(msg(e)));
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const run = async (label, fn, ok) => {
    setBusy(label);
    try { await call(fn); toast.success(ok); load(); } catch (e) { toast.error(msg(e)); load(); } finally { setBusy(''); }
  };
  const save = (e) => {
    e.preventDefault();
    run('save', () => gstAPI.saveDelivery({ ca: { name: f.caName, whatsapp: f.caWhatsapp }, ownerWhatsapp: f.ownerWhatsapp, enabled: f.enabled, sendDay: Number(f.sendDay) }), 'Saved');
  };

  if (!st) return <div className="spinner" />;
  return (
    <div className="card" style={{ padding: 16, marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>CA delivery (monthly, automatic)</h3>
      <p style={{ opacity: 0.7, marginTop: 0 }}>
        On the chosen day of every month (9:00 AM) the previous month&apos;s GSTR-1 Excel is sent to your CA on WhatsApp from the CRM&apos;s connected number.
        {st.settings.enabledFrom && f.enabled ? ` First automatic send after ${dmy(st.settings.enabledFrom)}.` : ''}
      </p>
      <form onSubmit={save}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: '1 1 220px' }}><label className="form-label">CA name</label><input className="form-control" value={f.caName} onChange={(e) => setF({ ...f, caName: e.target.value })} /></div>
          <div className="form-group" style={{ flex: '1 1 220px' }}><label className="form-label">CA WhatsApp number</label><input className="form-control" value={f.caWhatsapp} onChange={(e) => setF({ ...f, caWhatsapp: e.target.value })} placeholder="10-digit mobile" /></div>
          <div className="form-group" style={{ flex: '1 1 220px' }}><label className="form-label">Your WhatsApp (copy + test)</label><input className="form-control" value={f.ownerWhatsapp} onChange={(e) => setF({ ...f, ownerWhatsapp: e.target.value })} placeholder="10-digit mobile" /></div>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
          Send automatically on day
          <select className="form-control" style={{ width: 80 }} value={f.sendDay} onChange={(e) => setF({ ...f, sendDay: e.target.value })}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          of every month
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
          <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={() => run('test', () => gstAPI.deliveryTest(), 'Test sent to your WhatsApp')}>{busy === 'test' ? 'Sending…' : 'Send test to me'}</button>
          <span style={{ marginLeft: 12 }}>
            <input className="form-control" style={{ display: 'inline-block', width: 170 }} type="month" value={month} onChange={(e) => setMonth(e.target.value)} />{' '}
            <button type="button" className="btn btn-secondary" disabled={!!busy || !month}
              onClick={() => window.confirm(`Send the ${month} report to the CA now?`) && run('send', () => gstAPI.deliverySend(month), `${month} sent to the CA`)}>
              {busy === 'send' ? 'Sending…' : 'Send to CA now'}
            </button>
          </span>
        </div>
      </form>
      <h4>Recent sends</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr>{['Month', 'Status', 'Sent', 'Tries', 'Note'].map((h) => <th key={h} style={{ textAlign: 'left', padding: 4 }}>{h}</th>)}</tr></thead>
        <tbody>
          {st.dispatches.map((d) => (
            <tr key={d.key} style={{ borderTop: '1px solid rgba(128,128,128,.25)' }}>
              <td style={{ padding: 4 }}>{d.month}</td><td style={{ padding: 4 }}>{STATUS[d.status] || d.status}{d.revised ? ' (revised)' : ''}</td>
              <td style={{ padding: 4 }}>{d.sentAt ? dmy(d.sentAt) : '—'}</td><td style={{ padding: 4 }}>{d.attempts}</td>
              <td style={{ padding: 4 }}>{d.status === 'sent' && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} style={{ marginRight: 8 }}
                  onClick={() => window.confirm(`Open ${d.month} for changes? You can then edit or delete its bills, and must resend the report to the CA afterwards.`) && run('reopen', () => gstAPI.deliveryReopen(d.month), `${d.month} is open for changes`)}>
                  Open for changes
                </button>
              )}
              {d.status === 'reopened' ? 'Make your changes, then use "Send to CA now" for this month' : d.changedSinceSent ? 'Bills changed since it was sent — use "Send to CA now" to resend' : d.lastError || ''}</td>
            </tr>
          ))}
          {st.dispatches.length === 0 && <tr><td colSpan={5} style={{ padding: 8, opacity: 0.6 }}>Nothing sent yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
